<template>
  <div class="min-h-screen bg-surface">
    <!-- Hero Section -->
    <Section class="pt-24 pb-16 bg-white">
      <div class="container mx-auto px-4 max-w-4xl text-center">
        <h1 class="text-4xl md:text-5xl font-bold text-ink mb-6">
          {{ $t('howItWorks.title') }}
        </h1>
        <p class="text-xl text-muted mb-8 max-w-3xl mx-auto">
          {{ $t('howItWorks.subtitle') }}
        </p>
      </div>
    </Section>

    <!-- Steps Section -->
    <Section class="py-20">
      <div class="container mx-auto px-4 max-w-6xl">
        <div class="grid gap-16">
          <!-- Step 1: Connect -->
          <div class="flex flex-col lg:flex-row items-center gap-12">
            <div class="lg:w-1/2 order-2 lg:order-1">
              <div class="flex items-center gap-4 mb-6">
                <div class="w-12 h-12 bg-brand rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                  1
                </div>
                <h3 class="text-2xl font-bold text-ink">{{ $t('howItWorks.steps.connect.title') }}</h3>
              </div>
              <p class="text-lg text-muted mb-6">
                {{ $t('howItWorks.steps.connect.description') }}
              </p>
            </div>
            <div class="lg:w-1/2 order-1 lg:order-2">
              <div class="flex justify-center items-center gap-6 flex-wrap">
                <div v-for="integration in integrations" :key="integration.key" class="w-20 h-20 bg-white rounded-lg flex items-center justify-center p-2 shadow-sm border hover:shadow-md transition-shadow">
                  <img 
                    :src="integration.logo" 
                    :alt="`${integration.name} logo`"
                    class="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Step 2: Centralize -->
          <div class="flex flex-col lg:flex-row items-center gap-12">
            <div class="lg:w-1/2">
              <div class="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 h-80 flex items-center justify-center">
                <div class="text-center">
                  <div class="w-24 h-24 bg-brand rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg class="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                    </svg>
                  </div>
                  <h4 class="text-xl font-semibold text-ink">Unified Dashboard</h4>
                </div>
              </div>
            </div>
            <div class="lg:w-1/2">
              <div class="flex items-center gap-4 mb-6">
                <div class="w-12 h-12 bg-brand rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                  2
                </div>
                <h3 class="text-2xl font-bold text-ink">{{ $t('howItWorks.steps.centralize.title') }}</h3>
              </div>
              <p class="text-lg text-muted">
                {{ $t('howItWorks.steps.centralize.description') }}
              </p>
            </div>
          </div>

          <!-- Step 3: Optimize -->
          <div class="flex flex-col lg:flex-row items-center gap-12">
            <div class="lg:w-1/2 order-2 lg:order-1">
              <div class="flex items-center gap-4 mb-6">
                <div class="w-12 h-12 bg-brand rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                  3
                </div>
                <h3 class="text-2xl font-bold text-ink">{{ $t('howItWorks.steps.optimize.title') }}</h3>
              </div>
              <p class="text-lg text-muted">
                {{ $t('howItWorks.steps.optimize.description') }}
              </p>
            </div>
            <div class="lg:w-1/2 order-1 lg:order-2">
              <div class="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-8 h-80 flex items-center justify-center">
                <div class="text-center">
                  <div class="w-24 h-24 bg-brand rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg class="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                    </svg>
                  </div>
                  <h4 class="text-xl font-semibold text-ink">AI-Powered Optimization</h4>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 4: Grow -->
          <div class="flex flex-col lg:flex-row items-center gap-12">
            <div class="lg:w-1/2">
              <div class="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 h-80 flex items-center justify-center">
                <div class="text-center">
                  <div class="w-24 h-24 bg-brand rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg class="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                    </svg>
                  </div>
                  <h4 class="text-xl font-semibold text-ink">Growth Analytics</h4>
                </div>
              </div>
            </div>
            <div class="lg:w-1/2">
              <div class="flex items-center gap-4 mb-6">
                <div class="w-12 h-12 bg-brand rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                  4
                </div>
                <h3 class="text-2xl font-bold text-ink">{{ $t('howItWorks.steps.grow.title') }}</h3>
              </div>
              <p class="text-lg text-muted">
                {{ $t('howItWorks.steps.grow.description') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Section>

    <!-- CTA Section -->
    <Section class="py-20 bg-white">
      <div class="container mx-auto px-4 max-w-4xl text-center">
        <h2 class="text-3xl md:text-4xl font-bold text-ink mb-6">
          {{ $t('howItWorks.cta.title') }}
        </h2>
        <p class="text-xl text-muted mb-8">
          {{ $t('howItWorks.cta.description') }}
        </p>
        <BaseButton variant="primary" size="lg" @click="$router.push('/contact')">
          {{ $t('common.requestDemo') }}
        </BaseButton>
      </div>
    </Section>
  </div>
</template>

<script setup lang="ts">
import Section from '@/components/Section.vue'
import BaseButton from '@/components/BaseButton.vue'
import { integrations } from '@/data/integrations'
</script>